import { RowButton, TextButton } from "@/components/ui/pressable";
import { IconButton } from "@/components/ui/icon-button";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useInfiniteGetList,
  useNotify,
  type Identifier,
} from "ra-core";
import { useSearchParams } from "react-router";
import {
  CheckCircle2,
  CornerDownRight,
  History,
  Link2,
  MessageSquare,
  MoreHorizontal,
  Paperclip,
  Pin,
  PinOff,
  RotateCcw,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { ContactNote } from "@/components/atomic-crm/types";
import { NoteAttachments } from "@/components/atomic-crm/notes/NoteAttachments";
import { useMemberNames } from "@/modules/threads/useMemberNames";
import { ThreadRichText } from "@/modules/threads/ThreadRichText";
import {
  ThreadEditor,
  type ThreadDraftFile,
} from "@/modules/threads/ThreadEditor";
import { ThreadReactions } from "@/modules/threads/ThreadReactions";
import {
  createThreadNote,
  InlineReplyComposer,
} from "@/modules/threads/ThreadReplyComposer";
import {
  SeenByChip,
  ThreadHistoryDialog,
} from "@/modules/threads/ThreadAuditViews";
import {
  useMarkThreadsViewed,
  useThreadViews,
} from "@/modules/threads/useThreadViews";
import {
  formatAuditDate,
  type ThreadView,
} from "@/modules/threads/threadAudit";
import {
  useNoteReactions,
  type ReactionSummary,
} from "@/modules/threads/useNoteReactions";
import type { ThreadTarget } from "@/modules/threads/threadTargets";
import {
  filterThreads,
  groupByDay,
  buildReplyTree,
  isContinuationReply,
  type ReplyNode,
  sortThreads,
  splitPinnedThreads,
  THREAD_FILTERS,
  threadNoteKey,
  threadPlainText,
  type Thread,
  type ThreadFilter,
  type ThreadNote,
  type ThreadSort,
} from "@/modules/threads/threadModel";
import {
  useInvalidateThreads,
  useRecordThreads,
  type ThreadScope,
} from "@/modules/threads/useRecordThreads";
import {
  clientTimelineLabel,
  type ClientTimelineEvent,
} from "@/modules/clients/clientTimelineEvents";

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("") || "?";

const timeOf = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

type ReactionsByNote = Map<string, ReactionSummary[]>;

/** Project events shown between threads (tasks, meetings, invoices…). */
type FeedItem =
  | { type: "thread"; date: string; thread: Thread }
  | { type: "event"; date: string; event: ClientTimelineEvent };

/**
 * Team conversations of a project, an account or a person. Each thread is a
 * note with replies; "@" mentions a teammate (notified), reactions answer
 * without a message, important threads are pinned on top and finished ones
 * are resolved.
 */
export const RecordThreadsPanel = ({
  scope,
  targets,
  defaultTargetKey,
  showTargetLabels = true,
  systemEventsDealId,
}: {
  scope: ThreadScope;
  /** Where new threads can be posted (first = default). */
  targets: ThreadTarget[];
  defaultTargetKey?: string;
  /** Show the person / project of each thread (account and contact views). */
  showTargetLabels?: boolean;
  /** Project view: show the project's events between threads. */
  systemEventsDealId?: Identifier;
}) => {
  const { identity } = useGetIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusKey = searchParams.get("thread");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ThreadFilter>("all");
  // Chat order: newest at the bottom, next to the composer.
  const [sort, setSort] = useState<ThreadSort>("oldest");
  const listRef = useRef<HTMLDivElement>(null);
  const scrolledRef = useRef(false);
  const { threads, isPending } = useRecordThreads(scope);
  const memberNames = useMemberNames();
  const allNotes = useMemo(
    () => threads.flatMap((thread) => [thread.root, ...thread.replies]),
    [threads],
  );
  const reactions = useNoteReactions(allNotes);
  const roots = useMemo(() => threads.map((thread) => thread.root), [threads]);
  const views = useThreadViews(roots);
  const markViewed = useMarkThreadsViewed();

  const { data: eventPages } = useInfiniteGetList<ClientTimelineEvent>(
    "client_activity_timeline",
    {
      filter: { "deal_id@eq": systemEventsDealId ?? -1 },
      sort: { field: "occurred_at", order: "DESC" },
      pagination: { page: 1, perPage: 50 },
    },
    { enabled: systemEventsDealId != null, staleTime: 30_000 },
  );
  const events = useMemo(
    () =>
      (eventPages?.pages.flatMap((page) => page.data) ?? []).filter(
        // Notes are the threads themselves.
        (event) => event.kind !== "note" && event.kind !== "contact",
      ),
    [eventPages],
  );

  const targetLabel = (note: ThreadNote) => {
    const key =
      note.resource === "deal_notes"
        ? `deal:${note.deal_id}`
        : `contact:${note.contact_id}`;
    return targets.find((target) => target.key === key)?.label ?? null;
  };

  const { pinned, feed } = useMemo(() => {
    const visible = sortThreads(
      filterThreads(threads, {
        filter,
        search,
        memberId: identity?.id,
        labels: (thread) => targetLabel(thread.root) ?? "",
      }),
      sort,
    );
    const split = splitPinnedThreads(visible);
    const dateOf = (thread: Thread) =>
      sort === "activity" ? thread.lastActivityAt : thread.root.date;
    const items: FeedItem[] = split.rest.map((thread) => ({
      type: "thread",
      date: dateOf(thread),
      thread,
    }));
    // Events only in the plain chronological view.
    if (systemEventsDealId != null && filter === "all" && !search.trim()) {
      for (const event of events) {
        items.push({ type: "event", date: event.occurred_at, event });
      }
      items.sort((a, b) =>
        sort === "oldest"
          ? a.date.localeCompare(b.date)
          : b.date.localeCompare(a.date),
      );
    }
    return { pinned: split.pinned, feed: items };
    // targetLabel depends on targets only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, filter, search, identity?.id, sort, targets, events]);

  // Open at the latest messages (unless a link points at one thread).
  useEffect(() => {
    if (isPending || scrolledRef.current || focusKey) return;
    scrolledRef.current = true;
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [isPending, focusKey]);

  // One composer: Reply on a thread switches it to reply mode.
  const [replyTo, setReplyTo] = useState<{
    root: ThreadNote;
    target: ThreadNote;
  } | null>(null);
  const replyKey = replyTo ? threadNoteKey(replyTo.root) : null;

  const closeFocus = () => {
    if (!focusKey) return;
    const next = new URLSearchParams(searchParams);
    next.delete("thread");
    setSearchParams(next, { replace: true });
  };

  const renderThread = (thread: Thread) => (
    <ThreadCard
      key={threadNoteKey(thread.root)}
      thread={thread}
      memberNames={memberNames}
      reactions={reactions}
      label={showTargetLabels ? targetLabel(thread.root) : null}
      focused={focusKey === threadNoteKey(thread.root)}
      onFocusHandled={closeFocus}
      onReply={(target) => setReplyTo({ root: thread.root, target })}
      replyTarget={
        replyKey === threadNoteKey(thread.root) ? replyTo!.target : null
      }
      onReplyDone={() => setReplyTo(null)}
      views={views.get(threadNoteKey(thread.root)) ?? []}
      onSeen={() => markViewed(thread.root)}
      replyActive={replyKey === threadNoteKey(thread.root)}
      dimmed={replyKey != null && replyKey !== threadNoteKey(thread.root)}
    />
  );

  const empty = !pinned.length && !feed.length;

  return (
    // Fills the tab: filters and pinned on top, the threads scroll in the
    // middle, the composer stays at the bottom (like a chat).
    <div className="flex h-full min-h-[28rem] flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <div className="relative min-w-48 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search threads…"
            aria-label="Search threads"
            className="h-8 pl-8"
          />
        </div>
        <Select
          value={filter}
          onValueChange={(value) => setFilter(value as ThreadFilter)}
        >
          <SelectTrigger className="h-8 w-36" aria-label="Filter threads">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {THREAD_FILTERS.map((entry) => (
              <SelectItem key={entry.id} value={entry.id}>
                {entry.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={sort}
          onValueChange={(value) => setSort(value as ThreadSort)}
        >
          <SelectTrigger className="h-8 w-40" aria-label="Sort threads">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="activity">Latest activity</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {pinned.length ? (
        <section className="max-h-56 shrink-0 space-y-3 overflow-y-auto overscroll-contain rounded-xl border border-warning/40 bg-warning/10 p-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-warning-text uppercase">
            <Pin className="size-3.5" />
            Pinned
          </p>
          {pinned.map(renderThread)}
        </section>
      ) : null}

      <div
        ref={listRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
      >
        {isPending ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : empty ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">
            <MessageSquare className="size-6" />
            {threads.length === 0
              ? "No threads yet. Start one below — type @ to bring in a teammate."
              : "No threads match these filters."}
          </div>
        ) : (
          <div className="space-y-4">
            {groupByDay(feed, (item) => item.date).map((group) => (
              <section key={group.label} className="space-y-3">
                <DaySeparator label={group.label} />
                {group.items.map((item) =>
                  item.type === "thread" ? (
                    renderThread(item.thread)
                  ) : (
                    <SystemEventLine
                      key={item.event.id}
                      event={item.event}
                      memberNames={memberNames}
                    />
                  ),
                )}
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Fixed at the bottom; only the threads above scroll. */}
      <div className="sticky bottom-0 z-10 shrink-0 bg-card">
        {replyTo ? null : (
          <ThreadComposer
            targets={targets}
            defaultTargetKey={defaultTargetKey}
          />
        )}
      </div>
    </div>
  );
};

const DaySeparator = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3" role="separator">
    <span className="h-px flex-1 bg-border" />
    <span className="rounded-full border bg-background px-3 py-0.5 text-xs font-medium text-muted-foreground">
      {label}
    </span>
    <span className="h-px flex-1 bg-border" />
  </div>
);

/** "Diana Castro · Invoice sent INV-0169 · 3:30 PM" in grey, like Slack. */
const SystemEventLine = ({
  event,
  memberNames,
}: {
  event: ClientTimelineEvent;
  memberNames: Map<string, string>;
}) => {
  const actor =
    event.actor_member_id != null
      ? memberNames.get(String(event.actor_member_id))
      : null;
  const label = clientTimelineLabel(event);
  return (
    <p className="flex flex-wrap items-baseline gap-x-1.5 px-4 text-xs text-muted-foreground">
      <span className="size-1.5 shrink-0 translate-y-[-1px] rounded-full bg-muted-foreground/40" />
      {actor ? (
        <span className="font-medium text-foreground">{actor}</span>
      ) : null}
      <span>{actor ? label.toLowerCase() : label}</span>
      {event.title ? <span className="truncate">· {event.title}</span> : null}
      <span className="ml-auto tabular-nums">{timeOf(event.occurred_at)}</span>
    </p>
  );
};

const ThreadComposer = ({
  targets,
  defaultTargetKey,
}: {
  targets: ThreadTarget[];
  defaultTargetKey?: string;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const invalidate = useInvalidateThreads();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<ThreadDraftFile[]>([]);
  const [targetKey, setTargetKey] = useState(
    defaultTargetKey ?? targets[0]?.key ?? "",
  );
  const [saving, setSaving] = useState(false);
  const target = targets.find((entry) => entry.key === targetKey) ?? targets[0];

  useEffect(() => {
    if (!targets.some((entry) => entry.key === targetKey)) {
      setTargetKey(defaultTargetKey ?? targets[0]?.key ?? "");
    }
  }, [targets, targetKey, defaultTargetKey]);

  const post = async () => {
    if ((!text.trim() && !files.length) || !target || saving) return;
    setSaving(true);
    try {
      await createThreadNote(dataProvider, {
        target,
        text,
        files,
        memberId: identity?.id,
      });
      setText("");
      setFiles([]);
      await invalidate();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not post", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!targets.length) return null;

  return (
    <ThreadEditor
      value={text}
      onChange={setText}
      onSubmit={() => void post()}
      files={files}
      onFilesChange={setFiles}
      rows={2}
      ariaLabel="New thread"
      placeholder="Start a thread… type @ to mention a teammate"
      className="bg-card"
      actions={
        <>
          {targets.length > 1 ? (
            <Select value={target?.key} onValueChange={setTargetKey}>
              <SelectTrigger className="h-7 w-52 text-xs" aria-label="Post to">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {targets.map((entry) => (
                  <SelectItem key={entry.key} value={entry.key}>
                    {entry.kind === "deal" ? "Project · " : "Person · "}
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Button
            type="button"
            size="sm"
            className="h-7"
            disabled={(!text.trim() && !files.length) || saving}
            isLoading={saving}
            onClick={() => void post()}
          >
            Post
          </Button>
        </>
      }
    />
  );
};

const ThreadCard = ({
  thread,
  memberNames,
  reactions,
  label,
  focused,
  onFocusHandled,
  onReply,
  replyActive,
  replyTarget,
  onReplyDone,
  dimmed,
  views,
  onSeen,
}: {
  thread: Thread;
  memberNames: Map<string, string>;
  reactions: ReactionsByNote;
  label: string | null;
  focused: boolean;
  onFocusHandled: () => void;
  /** Reply to the thread or to one of its replies. */
  onReply: (target: ThreadNote) => void;
  /** This thread is being answered: highlighted, replies open. */
  replyActive: boolean;
  /** The note being answered in this thread (reply box under it). */
  replyTarget: ThreadNote | null;
  /** Reply sent or cancelled. */
  onReplyDone: () => void;
  /** Another thread is being answered. */
  dimmed: boolean;
  /** Who saw this thread. */
  views: ThreadView[];
  /** Called once the thread is on screen. */
  onSeen: () => void;
}) => {
  const [open, setOpen] = useState(focused);
  const ref = useRef<HTMLElement>(null);
  const { root, replies } = thread;
  const lastReply = replies.at(-1);
  const resolved = Boolean(root.resolved_at);
  const replyTree = useMemo(() => buildReplyTree(replies), [replies]);
  const authorOf = (note: ThreadNote) =>
    memberNames.get(String(note.organization_member_id)) ?? "Team member";
  const replyBoxFor = (note: ThreadNote) =>
    replyTarget && String(replyTarget.id) === String(note.id) ? (
      <InlineReplyComposer
        root={root}
        target={note}
        targetAuthor={authorOf(note)}
        onDone={onReplyDone}
      />
    ) : null;
  const [historyOpen, setHistoryOpen] = useState(false);

  const onSeenRef = useRef(onSeen);
  onSeenRef.current = onSeen;

  // Seen: the thread is at least half on screen (recorded once).
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onSeenRef.current();
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (replyActive) setOpen(true);
  }, [replyActive]);

  useEffect(() => {
    if (!focused) return;
    setOpen(true);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(onFocusHandled, 1500);
    return () => window.clearTimeout(timer);
  }, [focused, onFocusHandled]);

  return (
    <article
      ref={ref}
      className={cn(
        "rounded-xl border bg-card transition-[box-shadow,opacity]",
        (focused || replyActive) && "ring-2 ring-primary/40",
        dimmed && "opacity-40",
        resolved && "bg-muted/30",
      )}
    >
      <ThreadNoteBlock
        note={root}
        memberNames={memberNames}
        reactions={reactions.get(threadNoteKey(root)) ?? []}
        label={label}
        className={cn("p-4 pb-3", resolved && "opacity-70")}
        onReply={() => onReply(root)}
        seenBy={<SeenByChip views={views} memberNames={memberNames} />}
        repliesToggle={
          replies.length ? (
            <TextButton tone="accent" size="xs" onClick={() => setOpen(!open)}>
              <MessageSquare className="size-3.5" />
              {open
                ? "Hide replies"
                : `${replies.length} repl${replies.length === 1 ? "y" : "ies"}`}
            </TextButton>
          ) : null
        }
        headerExtra={
          <>
            {root.pinned_at ? (
              <Pin className="size-3.5 text-warning-text" aria-label="Pinned" />
            ) : null}
            {resolved ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-success px-2 py-0.5 text-2xs font-medium text-success-foreground">
                <CheckCircle2 className="size-3" />
                Resolved
                {root.resolved_by != null
                  ? ` by ${memberNames.get(String(root.resolved_by)) ?? "teammate"}`
                  : ""}
              </span>
            ) : null}
          </>
        }
        menu={
          <ThreadMenu root={root} onShowHistory={() => setHistoryOpen(true)} />
        }
      />

      {replyTarget && String(replyTarget.id) === String(root.id) ? (
        <div className="px-4 pb-2">{replyBoxFor(root)}</div>
      ) : null}

      {!open && lastReply ? (
        <RowButton
          layout="none"
          density="none"
          className="flex w-full items-start gap-1.5 px-4 pb-1 text-left text-sm text-muted-foreground hover:text-foreground"
          onClick={() => setOpen(true)}
        >
          <CornerDownRight className="mt-0.5 size-3.5 shrink-0" />
          <span className="line-clamp-1 italic">
            <span className="font-medium not-italic">
              {memberNames.get(String(lastReply.organization_member_id)) ??
                "Someone"}
              :
            </span>{" "}
            {threadPlainText(lastReply.text)}
          </span>
        </RowButton>
      ) : null}

      {open && replies.length ? (
        <div className="border-t bg-muted/20 px-4 py-2">
          <ReplyBranch
            nodes={replyTree}
            render={(node, index, siblings) => (
              <>
                <ThreadNoteBlock
                  note={node.note}
                  memberNames={memberNames}
                  reactions={reactions.get(threadNoteKey(node.note)) ?? []}
                  label={null}
                  compact
                  continuation={
                    node.children.length === 0 &&
                    isContinuationReply(siblings[index - 1]?.note, node.note) &&
                    siblings[index - 1]?.children.length === 0
                  }
                  className="py-0.5"
                  onReply={() => onReply(node.note)}
                />
                {replyBoxFor(node.note)}
              </>
            )}
          />
          <TextButton
            tone="muted"
            size="xs"
            className="mt-1"
            onClick={() => {
              setOpen(false);
              onReplyDone();
            }}
          >
            Collapse replies
          </TextButton>
        </div>
      ) : null}
      <ThreadHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        thread={thread}
        views={views}
        memberNames={memberNames}
      />
    </article>
  );
};

/** Max indent: deeper answers stay at this level (still in order). */
const MAX_REPLY_DEPTH = 3;

/**
 * Nested replies: each answer sits inside the reply it answers, with a
 * line on the left, like a conversation tree.
 */
const ReplyBranch = ({
  nodes,
  render,
}: {
  nodes: ReplyNode[];
  render: (node: ReplyNode, index: number, siblings: ReplyNode[]) => ReactNode;
}) => (
  <div className="space-y-0.5">
    {nodes.map((node, index) => (
      <div key={threadNoteKey(node.note)}>
        {render(node, index, nodes)}
        {node.children.length ? (
          <div
            className={cn(
              node.depth < MAX_REPLY_DEPTH - 1 &&
                "ml-3.5 border-l-2 border-border pl-3",
            )}
          >
            <ReplyBranch nodes={node.children} render={render} />
          </div>
        ) : null}
      </div>
    ))}
  </div>
);

/** Pin, resolve and copy link (any teammate). */
const ThreadMenu = ({
  root,
  onShowHistory,
}: {
  root: ThreadNote;
  onShowHistory: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const invalidate = useInvalidateThreads();

  const patch = async (data: Record<string, unknown>, done: string) => {
    try {
      await dataProvider.update(root.resource, {
        id: root.id,
        data,
        previousData: root,
      });
      await invalidate();
      notify(done);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not update", {
        type: "error",
      });
    }
  };

  const now = () => new Date().toISOString();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          aria-label="Thread actions"
          className="size-7 min-h-7 min-w-7"
        >
          <MoreHorizontal className="size-4" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {root.pinned_at ? (
          <DropdownMenuItem
            onClick={() =>
              void patch({ pinned_at: null, pinned_by: null }, "Unpinned")
            }
          >
            <PinOff className="size-4" />
            Unpin
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() =>
              void patch(
                { pinned_at: now(), pinned_by: identity?.id ?? null },
                "Pinned on top",
              )
            }
          >
            <Pin className="size-4" />
            Pin on top
          </DropdownMenuItem>
        )}
        {root.resolved_at ? (
          <DropdownMenuItem
            onClick={() =>
              void patch({ resolved_at: null, resolved_by: null }, "Reopened")
            }
          >
            <RotateCcw className="size-4" />
            Reopen
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() =>
              void patch(
                { resolved_at: now(), resolved_by: identity?.id ?? null },
                "Marked as resolved",
              )
            }
          >
            <CheckCircle2 className="size-4" />
            Mark as resolved
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onShowHistory}>
          <History className="size-4" />
          History
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("thread", threadNoteKey(root));
            void navigator.clipboard.writeText(url.toString());
            notify("Link copied");
          }}
        >
          <Link2 className="size-4" />
          Copy link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ThreadNoteBlock = ({
  note,
  memberNames,
  reactions,
  label,
  compact = false,
  continuation = false,
  headerExtra,
  menu,
  className,
  onReply,
  repliesToggle,
  seenBy,
}: {
  note: ThreadNote;
  memberNames: Map<string, string>;
  reactions: ReactionSummary[];
  label: string | null;
  compact?: boolean;
  /** Same author right after: hide the header, like a chat. */
  continuation?: boolean;
  headerExtra?: ReactNode;
  menu?: ReactNode;
  className?: string;
  /** "Reply" in the actions row (with reactions, Edit, Delete). */
  onReply?: () => void;
  /** "2 replies" toggle (thread root). */
  repliesToggle?: ReactNode;
  /** "Seen by 3" (thread root). */
  seenBy?: ReactNode;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const invalidate = useInvalidateThreads();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text ?? "");
  const [saving, setSaving] = useState(false);
  const author =
    memberNames.get(String(note.organization_member_id)) ?? "Team member";
  const isMine =
    identity?.id != null &&
    String(identity.id) === String(note.organization_member_id);
  const files = note.attachments?.length ?? 0;

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await dataProvider.update(note.resource, {
        id: note.id,
        data: { text: draft.trim() },
        previousData: note,
      });
      setEditing(false);
      await invalidate();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not save", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const message = note.parent_id
      ? "Delete this reply?"
      : "Delete this thread and its replies?";
    if (!window.confirm(message)) return;
    try {
      await dataProvider.delete(note.resource, {
        id: note.id,
        previousData: note,
      });
      await invalidate();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not delete", {
        type: "error",
      });
    }
  };

  const avatarSize = compact ? "size-7 text-2xs" : "size-8 text-xs";

  return (
    <div className={cn("group flex gap-2.5", className)}>
      {continuation ? (
        <span className={cn("shrink-0", compact ? "w-7" : "w-8")} />
      ) : (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-medium text-primary",
            avatarSize,
          )}
          aria-hidden
        >
          {initials(author)}
        </span>
      )}
      <div className="min-w-0 flex-1 space-y-1.5">
        {!continuation ? (
          <div className="flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-semibold">{author}</span>
            <span
              className="text-xs text-muted-foreground"
              title={`Created ${formatAuditDate(note.date)}`}
            >
              {timeOf(note.date)}
            </span>
            {note.edited_at ? (
              <span
                className="text-xs text-muted-foreground italic"
                title={`Edited by ${
                  note.edited_by != null
                    ? (memberNames.get(String(note.edited_by)) ?? "teammate")
                    : "teammate"
                } · ${formatAuditDate(note.edited_at)}`}
              >
                (edited)
              </span>
            ) : null}
            {label ? (
              <span className="truncate rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                {label}
              </span>
            ) : null}
            {headerExtra}
            {menu ? <span className="ml-auto">{menu}</span> : null}
          </div>
        ) : null}

        {editing ? (
          <ThreadEditor
            value={draft}
            onChange={setDraft}
            onSubmit={() => void save()}
            onCancel={() => {
              setDraft(note.text ?? "");
              setEditing(false);
            }}
            rows={2}
            autoFocus
            ariaLabel="Edit note"
            actions={
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7"
                  onClick={() => {
                    setDraft(note.text ?? "");
                    setEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-7"
                  isLoading={saving}
                  disabled={!draft.trim() || saving}
                  onClick={() => void save()}
                >
                  Save
                </Button>
              </>
            }
          />
        ) : note.text?.trim() ? (
          <ThreadRichText text={note.text} />
        ) : null}

        {files ? (
          <div className="space-y-1">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Paperclip className="size-3" />
              {files} file{files === 1 ? "" : "s"}
            </span>
            <NoteAttachments note={note as unknown as ContactNote} />
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {onReply && !editing ? (
            <TextButton tone="muted" size="xs" onClick={onReply}>
              <CornerDownRight className="size-3.5" />
              Reply
            </TextButton>
          ) : null}
          {repliesToggle}
          {seenBy}
          <ThreadReactions
            note={note}
            reactions={reactions}
            memberNames={memberNames}
          />
          {isMine && !editing ? (
            <div className="flex gap-3 text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              <TextButton
                tone="muted"
                size="inherit"
                onClick={() => setEditing(true)}
              >
                Edit
              </TextButton>
              <TextButton
                tone="destructive"
                size="inherit"
                onClick={() => void remove()}
              >
                Delete
              </TextButton>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
